import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {ContextualMenuControlsOption} from '@Components/contextual-menu/components/contextual-menu-controls-option';
import type {BaseControlItem} from '@Components/contextual-menu/components/controls';
import {PosterEditorItemAdjustmentOptions} from '@Panels/poster-editor-item-adjustments-panel/components/default-adjustment-options/default-adjustment-options.types';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import styles from './default-adjustment-options.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface DefaultPosterEditorItemAdjustmentOptionsProps {
  panelId: string;
  onClick(id: PosterEditorItemAdjustmentOptions): void;
  isForBackground?: boolean;
}

export function DefaultPosterEditorItemAdjustmentOptions({isForBackground = false, ...props}: DefaultPosterEditorItemAdjustmentOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  useEffect((): void => {
    updateOptionsPanelHeight(props.panelId, '144px');
  }, []);

  const showTintOption = (): boolean => {
    if (isForBackground) {
      return true;
    }
    return !(activeItem?.isImage() && activeItem?.isDrawing());
  };

  const getDataForControls = (): BaseControlItem[] => {
    const data = [];

    if (showTintOption()) {
      data.push({
        id: PosterEditorItemAdjustmentOptions.TINT,
        className: styles.items,
        icon: 'icon-tint',
        text: window.i18next.t('pmwjs_tint'),
        hasCustomColor: true,
        textClasses: `spacing-m-t-2 content-body`,
      });
    }

    if (!(activeItem?.isImage() && activeItem?.isIcon())) {
      data.push(
        {
          id: PosterEditorItemAdjustmentOptions.BRIGHTNESS,
          className: styles.items,
          icon: 'icon-brightness',
          text: window.i18next.t('pmwjs_brightness'),
          hasCustomColor: true,
          textClasses: `spacing-m-t-2 content-body`,
        },
        {
          id: PosterEditorItemAdjustmentOptions.CONTRAST,
          className: styles.items,
          icon: 'icon-contrast',
          text: window.i18next.t('pmwjs_contrast'),
          hasCustomColor: true,
          textClasses: `spacing-m-t-2 content-body`,
        },
        {
          id: PosterEditorItemAdjustmentOptions.VIBRANCE,
          className: styles.items,
          icon: 'icon-vibrance',
          text: window.i18next.t('pmwjs_vibrance'),
          hasCustomColor: true,
          textClasses: `spacing-m-t-2 content-body`,
        },
        {
          id: PosterEditorItemAdjustmentOptions.SATURATION,
          className: styles.items,
          icon: 'icon-saturation',
          text: window.i18next.t('pmwjs_saturation'),
          hasCustomColor: true,
          textClasses: `spacing-m-t-2 content-body`,
        },
        {
          id: PosterEditorItemAdjustmentOptions.GAMMA,
          className: styles.items,
          icon: 'icon-gamma',
          text: window.i18next.t('pmwjs_gamma'),
          hasCustomColor: true,
          textClasses: `spacing-m-t-2 content-body`,
        }
      );
    }

    data.push({
      id: PosterEditorItemAdjustmentOptions.MULTIPLY,
      className: styles.items,
      icon: 'icon-multiply',
      text: window.i18next.t('pmwjs_multiply'),
      hasCustomColor: true,
      textClasses: `spacing-m-t-2 content-body`,
    });

    return data;
  };

  const getDefaultContent = (): ReactElement => {
    return (
      <div className="flexbox">
        <ContextualMenuControlsOption
          id="item-adjustment-controls"
          optionsContainerClassName={styles.container}
          className={styles.container}
          containerClassName={`${styles.gap} ${styles.container} content-body spacing-p-l-4 spacing-p-r-4`}
          onClick={(id: PosterEditorItemAdjustmentOptions): void => {
            props.onClick(id);
          }}
          data={getDataForControls()}
        />
      </div>
    );
  };

  return getDefaultContent();
}
